/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { timeout } from 'ember-concurrency';
import { isEmpty } from '@ember/utils';
import ApiService from 'cio/services/api';
import AuthService from 'cio/services/auth';
import ImportModel from 'cio/models/import';
import { dropTask, restartableTask } from 'ember-concurrency';
import AnalyticsService from 'cio/services/analytics';
import { tracked } from '@glimmer/tracking';
import DS from 'ember-data';
import FeaturesService from 'ember-feature-flags/services/features';
import { buildWaiter } from '@ember/test-waiters';
import ApiErrorsService from 'cio/services/api-errors';
import GapiService from 'cio/services/gapi';
import OAuthService from 'cio/services/oauth';
import GoogleSheetsService from 'cio/services/google-sheets';
import { SheetObject } from 'cio/utils/google-sheets';

interface ImportGoogleSheetsArgs {
  import: ImportModel;
  onChange: (sheetData: SheetObject) => unknown;
}

export const OAUTH_INACCESSIBLE_URL_ERROR =
  "We weren't able to access that sheet. Please make sure your Google account has access to that sheet and try again.";

export const INACCESSIBLE_URL_ERROR =
  'Permission denied: ensure the sheet is public, or shared with cio_share@customer.io.';

export const INVALID_URL_ERROR =
  'That looks like an invalid URL. Make sure it starts with https://docs.google.com/spreadsheets.';

const TIMEOUT_DURATION = 250;

const pickerWaiter = buildWaiter('import-google-sheets:picker-waiter');

const isValidGoogleSheetsUrl = (url: string) => url.startsWith('https://docs.google.com/spreadsheets');

export default class ImportGoogleSheets extends Component<ImportGoogleSheetsArgs> {
  @service api!: ApiService;
  @service auth!: AuthService;
  @service analytics!: AnalyticsService;
  @service store!: DS.Store;
  @service features!: FeaturesService;
  @service apiErrors!: ApiErrorsService;
  @service gapi!: GapiService;
  @service oauth!: OAuthService;
  @service googleSheets!: GoogleSheetsService;

  @tracked url = '';
  @tracked errors: string[] | null = null;
  @tracked isValid = false;
  @tracked isChoosing = false;
  @tracked filename = '';
  @tracked sheetName = '';
  @tracked sheetId = '';
  @tracked spreadsheetName = '';
  @tracked spreadsheetId = '';
  @tracked pickerOpen = false;

  googleSheetsImage = new URL('cio/assets/images/google-sheets.png', import.meta.url).toString();

  get googleCredential() {
    return this.auth.user?.googleDriveCredential ?? null;
  }

  get sheetsEmailAccount() {
    return this.googleCredential?.account;
  }

  get needToSelectSpreadsheet() {
    return this.googleCredential && this.googleSheets.hasRequiredScopes && !this.spreadsheetId && !this.pickerOpen;
  }

  get needToSelectSheet() {
    return this.spreadsheetId && !this.sheetId;
  }

  get isDisabled() {
    if (this.args.import.type === 'segment') {
      return !this.auth.hasPermission('journeys/segments/edit');
    } else if (!this.args.import?.isNew) {
      return !this.auth.hasPermission('journeys/imports/edit');
    }
    return !this.auth.hasPermission('journeys/imports/create');
  }

  setDefaults() {
    this.errors = [];
    this.isValid = false;
  }

  @action
  toggleModal() {
    this.isChoosing = !this.isChoosing;
    this.setDefaults();
  }

  @action
  didChangeUrl(url: string) {
    this.url = url;

    if (isEmpty(url)) {
      this.setDefaults();
    } else {
      this.validate.perform();
    }
  }

  @action
  useSheet(evt?: Event) {
    evt?.preventDefault();

    this.args.onChange({
      sheetName: this.sheetName,
      sheetId: Number(this.sheetId),
      spreadsheetName: this.spreadsheetName,
      spreadsheetId: this.spreadsheetId,
    });

    this.isChoosing = false;
  }

  @action
  onPickerAction(data: google.picker.ResponseObject) {
    if (data.action === this.gapi.google!.picker.Action.PICKED) {
      let [selectedSpreadsheet] = data.docs;

      this.url = selectedSpreadsheet.url;
      this.spreadsheetName = selectedSpreadsheet.name;
      this.spreadsheetId = selectedSpreadsheet.id;

      this.fetchSheets.perform(this.spreadsheetId);
      this.pickerOpen = false;
    } else if (data.action === this.gapi.google!.picker.Action.CANCEL) {
      this.pickerOpen = false;
    }
  }

  @action
  onSheetSelect(value: string) {
    let sheetOption = this.fetchSheets.last?.value?.find((sheet) => sheet.value === value);

    this.sheetId = value;
    this.sheetName = sheetOption?.label ?? '';
  }

  @action
  resetSheet() {
    this.spreadsheetId = '';
    this.spreadsheetName = '';
    this.sheetId = '';
    this.sheetName = '';
  }

  validate = restartableTask(async () => {
    await timeout(TIMEOUT_DURATION);
    this.setDefaults();

    let { url: path } = this;

    if (!isValidGoogleSheetsUrl(path)) {
      return (this.errors = [INVALID_URL_ERROR]);
    }

    try {
      let response = await this.api.post('imports/validate', {
        ignoreErrors: true,
        data: { import: { path } },
      });

      this.isValid = true;
      this.sheetId = response.sheet_id;
      this.sheetName = response.sheet_name;
      this.spreadsheetName = response.spreadsheet_name;
      this.spreadsheetId = response.spreadsheet_id;
    } catch (e) {
      this.errors = [OAUTH_INACCESSIBLE_URL_ERROR];
    }

    return;
  });

  startPicker = dropTask(async () => {
    let pickerWaiterToken = pickerWaiter.beginAsync();

    await this.googleSheets.getUserAccessToken.perform();

    if (this.googleSheets.accessToken != null) {
      await this.googleSheets.checkTokenScopes.perform();
    }

    if (!this.googleSheets.hasRequiredScopes || !this.googleSheets.hasAuthorizedGoogleDrive) {
      pickerWaiter.endAsync(pickerWaiterToken);
      return;
    }

    await this.gapi.openSpreadsheetPicker(this.googleSheets.accessToken!, this.onPickerAction);
    pickerWaiter.endAsync(pickerWaiterToken);
  });

  fetchSheets = dropTask(async (spreadsheetId: string) => {
    let sheets = await this.googleSheets.fetchSheets.perform(spreadsheetId);

    this.sheetId = sheets[0].value;
    this.sheetName = sheets[0].label;

    return sheets;
  });
}
